import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import useSites from "../data/useSites";
import { ISite } from "../models/Site";
import {
  ISiteScopedItem,
  readRememberedSiteScope,
  rememberSiteScope,
  resolveSiteScope,
  scopeLocationsToSite,
} from "../utils/siteScope";

/**
 * Which depot a location picker should offer. Starts from the record's own site when known
 * (`preferredSiteId`), otherwise the site the user last chose on any picker, otherwise any site.
 * Only explicit choices are remembered, so opening a Moy vehicle doesn't flip a Campsie user's
 * default.
 */
export default function useSiteScope(preferredSiteId?: string | null) {
  const { sites } = useSites();
  const siteList: ISite[] = useMemo(() => sites.data?.data ?? [], [sites.data]);
  const siteIds = useMemo(() => siteList.map((s) => s.id), [siteList]);
  const hasSites = siteList.length > 0;

  const touched = useRef(false);
  const [rawSiteId, setRawSiteId] = useState<string>(() => preferredSiteId ?? readRememberedSiteScope());

  // The record's site usually arrives after first render (locations still loading), so adopt it
  // once it is known unless the user has already picked something.
  useEffect(() => {
    if (preferredSiteId && !touched.current) setRawSiteId(preferredSiteId);
  }, [preferredSiteId]);

  const siteId = resolveSiteScope(rawSiteId, siteIds);

  const setSiteId = useCallback((next: string) => {
    touched.current = true;
    setRawSiteId(next);
    rememberSiteScope(next);
  }, []);

  const scope = useCallback(
    <T extends ISiteScopedItem>(items: T[], keepId?: string | null) =>
      hasSites ? scopeLocationsToSite(items, siteId, keepId) : items,
    [hasSites, siteId]
  );

  return { siteId, setSiteId, hasSites, sites: siteList, scope };
}
